import React, { useState, useEffect } from 'react';
import { Users, Video, MessageSquare, Clock, TrendingUp } from 'lucide-react';
import { supabase } from '../../utils/supabase';
import { AdminStats } from '../../types';

interface ExtendedStats extends AdminStats {
  avgProcessingTime: number;
}

interface RecentActivity {
  id: string;
  type: 'user' | 'video';
  title: string;
  description: string;
  time: Date;
}

export function Overview() {
  const [stats, setStats] = useState<ExtendedStats>({
    totalUsers: 0,
    totalVideos: 0,
    totalCaptions: 0,
    avgProcessingTime: 0
  });
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchStats();
    fetchRecentActivity();
    const interval = setInterval(() => {
      fetchStats();
      fetchRecentActivity();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async () => {
    try {
      setError(null);
      
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const thirtyDaysAgoStr = thirtyDaysAgo.toISOString();

      const { data: adminUsers, error: adminError } = await supabase
        .from('profiles')
        .select('id')
        .eq('role', 'admin');

      if (adminError) throw adminError;

      const adminIds = adminUsers?.map(admin => admin.id) || [];

      const [
        { data: usersData, error: usersError },
        { data: transcriptionsData, error: transcriptionsError },
        { data: processingData, error: processingError }
      ] = await Promise.all([
        supabase
          .from('profiles')
          .select('id', { count: 'exact' }),

        supabase
          .from('transcription_jobs')
          .select('id', { count: 'exact' })
          .gte('created_at', thirtyDaysAgoStr),

        supabase
          .from('transcription_jobs')
          .select('processing_duration_seconds')
          .not('processing_duration_seconds', 'is', null)
          .gte('created_at', thirtyDaysAgoStr)
      ]);

      if (usersError) throw usersError;
      if (transcriptionsError) throw transcriptionsError;
      if (processingError) throw processingError;

      const totalProcessingTime = processingData?.reduce((sum, job) => 
        sum + (job.processing_duration_seconds || 0), 0) || 0;
      const avgProcessingTime = processingData?.length 
        ? Math.round(totalProcessingTime / processingData.length / 60)
        : 0;

      setStats({
        totalUsers: usersData?.count || 0,
        totalVideos: transcriptionsData?.count || 0,
        totalCaptions: transcriptionsData?.count || 0,
        avgProcessingTime
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
      setError('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentActivity = async () => {
    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const thirtyDaysAgoStr = thirtyDaysAgo.toISOString();

      const { data: recentUsers, error: usersError } = await supabase
        .from('profiles')
        .select('id, email, created_at')
        .gte('created_at', thirtyDaysAgoStr)
        .order('created_at', { ascending: false })
        .limit(3);

      if (usersError) throw usersError;

      const { data: recentVideos, error: videosError } = await supabase
        .from('transcription_jobs')
        .select('id, title, created_at')
        .gte('created_at', thirtyDaysAgoStr)
        .order('created_at', { ascending: false })
        .limit(3);

      if (videosError) throw videosError;

      const activities: RecentActivity[] = [
        ...(recentUsers?.map(user => ({
          id: user.id,
          type: 'user' as const,
          title: 'New User Registration',
          description: `${user.email} joined the platform`,
          time: new Date(user.created_at)
        })) || []),
        ...(recentVideos?.map(video => ({
          id: video.id,
          type: 'video' as const,
          title: 'Video Processed',
          description: `'${video.title}' was successfully transcribed`,
          time: new Date(video.created_at)
        })) || [])
      ].sort((a, b) => b.time.getTime() - a.time.getTime())
      .slice(0, 5);

      setRecentActivity(activities);
    } catch (error) {
      console.error('Error fetching recent activity:', error);
      setError('Failed to load recent activity');
    }
  };

  if (loading) {
    return (
      <div className="bg-card shadow rounded-lg p-6 border">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-secondary rounded w-1/4"></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-32 bg-secondary rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-destructive/10 border-l-4 border-destructive p-4 rounded-md">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        <StatCard
          title="Total Users"
          value={stats.totalUsers}
          icon={<Users className="w-6 h-6" />}
          color="bg-blue-500 dark:bg-blue-600"
        />
        <StatCard
          title="Videos (30 Days)"
          value={stats.totalVideos}
          icon={<Video className="w-6 h-6" />}
          color="bg-green-500 dark:bg-green-600"
        />
        <StatCard
          title="Captions (30 Days)"
          value={stats.totalCaptions}
          icon={<MessageSquare className="w-6 h-6" />}
          color="bg-purple-500 dark:bg-purple-600"
        />
        <StatCard
          title="Avg. Processing Time"
          value={`${stats.avgProcessingTime} min`}
          icon={<Clock className="w-6 h-6" />}
          color="bg-amber-500 dark:bg-amber-600"
        />
      </div>

      <div className="bg-card shadow rounded-lg overflow-hidden border">
        <div className="px-4 py-5 sm:px-6 border-b">
          <h3 className="text-lg font-medium text-foreground">Recent Activity</h3>
        </div>
        <div className="px-4 py-5 sm:p-6">
          <div className="space-y-4">
            {recentActivity.length > 0 ? (
              recentActivity.map((activity) => (
                <ActivityItem
                  key={activity.id}
                  title={activity.title}
                  description={activity.description}
                  time={formatTimeAgo(activity.time)}
                  icon={getActivityIcon(activity.type)}
                  color={getActivityColor(activity.type)}
                />
              ))
            ) : (
              <p className="text-center text-muted-foreground">No recent activity</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, icon, color }: { 
  title: string; 
  value: string | number; 
  icon: React.ReactNode;
  color: string;
}) {
  return (
    <div className="bg-card overflow-hidden shadow rounded-lg border">
      <div className="p-5">
        <div className="flex items-center">
          <div className={`flex-shrink-0 rounded-md p-3 ${color}`}>
            {React.cloneElement(icon as React.ReactElement, { className: 'w-6 h-6 text-white' })}
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-muted-foreground truncate">{title}</dt>
              <dd className="text-2xl font-semibold text-foreground">{value}</dd>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}

function ActivityItem({ title, description, time, icon, color }: {
  title: string;
  description: string;
  time: string;
  icon: React.ReactNode;
  color: string;
}) {
  return (
    <div className="flex space-x-3">
      <div className={`flex-shrink-0 ${color}`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <div className="flex-shrink-0 whitespace-nowrap text-sm text-muted-foreground">
        {time}
      </div>
    </div>
  );
}

function getActivityIcon(type: 'user' | 'video') {
  switch (type) {
    case 'user':
      return <Users className="w-5 h-5" />;
    case 'video':
      return <Video className="w-5 h-5" />;
  }
}

function getActivityColor(type: 'user' | 'video') {
  switch (type) {
    case 'user':
      return 'text-blue-500 dark:text-blue-400';
    case 'video':
      return 'text-green-500 dark:text-green-400';
  }
}

function formatTimeAgo(date: Date) {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'just now';
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays}d ago`;
  }

  return date.toLocaleDateString();
}